import { useAnalytics } from 'contexts/Analytics/Analytics';
import { useApi } from 'contexts/Api/Api';
import { validateEmail } from 'helpers';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
    Backdrop,
    CheckboxWrapper,
    CenteredColumn,
    CloseButton,
    ContainedButton,
    Form,
    FormFields,
    FormHeader,
    MassiveButton,
    ModalContainer,
    ModalContent,
    ModalDivider,
    OAuthButton,
    OAuthIconWrap,
    Row,
    SectionRule,
    Text,
    TextActionButton,
    TextField,
    Title,
} from './LandingAuth.styled';

type LoginFormState = {
    email: string;
    password: string;
    rememberMe: boolean;
};

type JoinFormState = {
    email: string;
    name: string;
    password: string;
    subscribeToNews: boolean;
};

const defaultLoginForm: LoginFormState = {
    email: '',
    password: '',
    rememberMe: false,
};

const defaultJoinForm: JoinFormState = {
    email: '',
    name: '',
    password: '',
    subscribeToNews: true,
};

function getNameParts(name: string) {
    const normalized = name.trim().replace(/\s+/g, ' ');
    const parts = normalized.split(' ').filter(Boolean);
    const first_name = parts[0] || '';
    const last_name = parts.length > 1 ? parts.slice(1).join(' ') : null;
    return {
        first_name,
        last_name,
    };
}

function GoogleIcon() {
    return (
        <OAuthIconWrap aria-hidden>
            <svg height="20" viewBox="0 0 18 18" width="20">
                <path
                    d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.2 1.12-.84 2.06-1.8 2.7v2.24h2.92c1.72-1.58 2.68-3.92 2.68-6.58z"
                    fill="#4285F4"
                />
                <path
                    d="M9 18c2.44 0 4.5-.8 6-2.22l-2.92-2.24c-.8.54-1.82.86-3.08.86-2.36 0-4.36-1.58-5.08-3.72H.92v2.3A9 9 0 0 0 9 18z"
                    fill="#34A853"
                />
                <path
                    d="M3.92 10.68A5.4 5.4 0 0 1 3.64 9c0-.58.1-1.14.28-1.68V5.02H.92A9 9 0 0 0 0 9c0 1.44.34 2.8.92 3.98l3-2.3z"
                    fill="#FBBC05"
                />
                <path
                    d="M9 3.58c1.34 0 2.54.46 3.48 1.36l2.6-2.6C13.5.9 11.44 0 9 0A9 9 0 0 0 .92 5.02l3 2.3C4.64 5.18 6.64 3.58 9 3.58z"
                    fill="#EA4335"
                />
            </svg>
        </OAuthIconWrap>
    );
}

export default function LandingAuth() {
    const api = useApi();
    const { trackActionEvent } = useAnalytics();
    const [error, setError] = useState('');
    const [joinForm, setJoinForm] = useState<JoinFormState>(defaultJoinForm);
    const [loginForm, setLoginForm] = useState<LoginFormState>(defaultLoginForm);

    const mode = api.authModalScreen;
    const loading = api.loading;

    const open = useMemo(() => mode === 'login' || mode === 'join', [mode]);

    // Link out to orca-front's dedicated reset page. Host-aware base mirrors the
    // staging-vs-prod convention used for the auth API base (see contexts/Api/Api.tsx),
    // prefilling the login email when present.
    const forgotPasswordUrl = useMemo(() => {
        const base =
            typeof window !== 'undefined' && window.location.hostname === 'staging.kromatic.com'
                ? 'https://staging.kromatic.com'
                : 'https://kromatic.com';
        const email = loginForm.email.trim();
        return `${base}/auth/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`;
    }, [loginForm.email]);

    const prevOpenRef = useRef(false);

    useEffect(() => {
        if (open && !prevOpenRef.current) {
            void trackActionEvent('signup_modal_opened', {
                resource_type: 'tool',
                screen: mode,
                source: 'landing_auth',
            });
        }
        prevOpenRef.current = open;
    }, [open, mode, trackActionEvent]);

    useEffect(() => {
        if (!open) {
            setError('');
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }

        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === 'Escape' && !loading) {
                api.hideAuthModal();
            }
        }

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [api, loading, open]);

    async function handleLoginSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        void trackActionEvent('signup_form_submitted', {
            resource_type: 'tool',
            screen: 'login',
            source: 'landing_auth',
        });
        setError('');

        if (!validateEmail(loginForm.email)) {
            setError('Please enter a valid email address.');
            return;
        }
        if (!loginForm.password) {
            setError('Please enter your password.');
            return;
        }

        try {
            await api.login({
                email: loginForm.email.trim(),
                password: loginForm.password,
                remember_me: loginForm.rememberMe,
            });
            setLoginForm(defaultLoginForm);
            api.hideAuthModal();
        } catch (submitError: any) {
            setError(submitError?.message || 'Unable to log in right now.');
        }
    }

    async function handleJoinSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        void trackActionEvent('signup_form_submitted', {
            resource_type: 'tool',
            screen: 'join',
            source: 'landing_auth',
        });
        setError('');

        const parsedName = getNameParts(joinForm.name);
        if (!parsedName.first_name || parsedName.first_name.length < 2) {
            setError('Please enter your full name.');
            return;
        }
        if (!validateEmail(joinForm.email)) {
            setError('Please enter a valid email address.');
            return;
        }
        if (!joinForm.password) {
            setError('Please enter a password.');
            return;
        }

        try {
            await api.join({
                email: joinForm.email.trim(),
                first_name: parsedName.first_name,
                last_name: parsedName.last_name,
                password: joinForm.password,
                remember_me: false,
                subscribe_to_news: joinForm.subscribeToNews,
            });
            setJoinForm(defaultJoinForm);
            api.hideAuthModal();
        } catch (submitError: any) {
            setError(submitError?.message || 'Unable to join right now.');
        }
    }

    function handleCloseModal() {
        if (!loading) {
            api.hideAuthModal();
        }
    }

    return (
        <>
            {open && (
                <ModalContainer data-testid="landing-auth-modal">
                    <ModalContent>
                        <CloseButton
                            aria-label="Close auth modal"
                            data-testid="landing-auth-close"
                            disabled={loading}
                            onClick={handleCloseModal}
                        >
                            <svg fill="currentColor" height="18" viewBox="0 0 24 24" width="18">
                                <path d="M18.3 5.71a.9959.9959 0 0 0-1.41 0L12 10.59 7.11 5.7a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41L10.59 12 5.7 16.89c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L12 13.41l4.89 4.89c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z" />
                            </svg>
                        </CloseButton>

                        <Form
                            $loading={loading}
                            onChange={() => setError('')}
                            onSubmit={mode === 'join' ? handleJoinSubmit : handleLoginSubmit}
                        >
                            {mode === 'login' && (
                                <>
                                    <FormHeader>
                                        <Title>Log in</Title>
                                        <OAuthButton
                                            data-testid="landing-login-oauth-google"
                                            disabled={loading}
                                            type="button"
                                            onClick={() => api.startGoogleOAuth('login')}
                                        >
                                            <GoogleIcon />
                                            Log in with Google
                                        </OAuthButton>
                                        <ModalDivider>or use your account</ModalDivider>
                                    </FormHeader>
                                    <FormFields>
                                        <TextField
                                            data-testid="landing-login-email"
                                            disabled={loading}
                                            name="email"
                                            placeholder="Work email"
                                            type="email"
                                            value={loginForm.email}
                                            onChange={e => {
                                                const { value } = e.currentTarget;
                                                setLoginForm(prev => ({ ...prev, email: value }));
                                            }}
                                        />
                                        <TextField
                                            data-testid="landing-login-password"
                                            disabled={loading}
                                            name="password"
                                            placeholder="Password"
                                            type="password"
                                            value={loginForm.password}
                                            onChange={e => {
                                                const { value } = e.currentTarget;
                                                setLoginForm(prev => ({ ...prev, password: value }));
                                            }}
                                        />
                                        {error && (
                                            <Text $error data-testid="landing-login-error">
                                                {error}
                                            </Text>
                                        )}
                                    </FormFields>
                                    <Row $gap={20} $marginBottom={36}>
                                        <CheckboxWrapper>
                                            <input
                                                checked={loginForm.rememberMe}
                                                disabled={loading}
                                                id="remember_me"
                                                name="remember_me"
                                                type="checkbox"
                                                onChange={e => {
                                                    const { checked } = e.currentTarget;
                                                    setLoginForm(prev => ({
                                                        ...prev,
                                                        rememberMe: checked,
                                                    }));
                                                }}
                                            />
                                            <span>Remember me</span>
                                        </CheckboxWrapper>
                                        <TextActionButton
                                            as="a"
                                            data-testid="landing-login-forgot-password"
                                            href={forgotPasswordUrl}
                                        >
                                            Forgot Password?
                                        </TextActionButton>
                                    </Row>
                                    <CenteredColumn>
                                        <MassiveButton
                                            data-testid="landing-login-submit"
                                            disabled={loading}
                                            type="submit"
                                        >
                                            Log in
                                        </MassiveButton>
                                    </CenteredColumn>
                                    <SectionRule />
                                    <CenteredColumn $gap={16}>
                                        <Text $size={18}>Not a member yet?</Text>
                                        <ContainedButton
                                            data-testid="landing-login-open-join"
                                            disabled={loading}
                                            type="button"
                                            onClick={() => api.showAuthModal('join')}
                                        >
                                            Join now
                                        </ContainedButton>
                                    </CenteredColumn>
                                </>
                            )}

                            {mode === 'join' && (
                                <>
                                    <FormHeader $marginTopBottom="0 20px">
                                        <Title>Join for free</Title>
                                        <Text $center $size={18}>
                                            Please create an account
                                            <br />
                                            to access Kromatic
                                        </Text>
                                        <Row $gap={8}>
                                            <Text $size={12}>Already have an account?</Text>
                                            <TextActionButton
                                                data-testid="landing-join-open-login"
                                                disabled={loading}
                                                type="button"
                                                onClick={() => api.showAuthModal('login')}
                                            >
                                                Log in
                                            </TextActionButton>
                                        </Row>
                                    </FormHeader>
                                    <Row>
                                        <OAuthButton
                                            data-testid="landing-join-oauth-google"
                                            disabled={loading}
                                            type="button"
                                            onClick={() => api.startGoogleOAuth('join', joinForm.subscribeToNews)}
                                        >
                                            <GoogleIcon />
                                            Sign up with Google
                                        </OAuthButton>
                                    </Row>
                                    <ModalDivider $margin="20px 0">or use your account</ModalDivider>
                                    <FormFields>
                                        <TextField
                                            data-testid="landing-join-name"
                                            disabled={loading}
                                            name="name"
                                            placeholder="Your name"
                                            value={joinForm.name}
                                            onChange={e => {
                                                const { value } = e.currentTarget;
                                                setJoinForm(prev => ({ ...prev, name: value }));
                                            }}
                                        />
                                        <TextField
                                            data-testid="landing-join-email"
                                            disabled={loading}
                                            name="email"
                                            placeholder="Work email"
                                            type="email"
                                            value={joinForm.email}
                                            onChange={e => {
                                                const { value } = e.currentTarget;
                                                setJoinForm(prev => ({ ...prev, email: value }));
                                            }}
                                        />
                                        <TextField
                                            data-testid="landing-join-password"
                                            disabled={loading}
                                            name="password"
                                            placeholder="Password"
                                            type="password"
                                            value={joinForm.password}
                                            onChange={e => {
                                                const { value } = e.currentTarget;
                                                setJoinForm(prev => ({ ...prev, password: value }));
                                            }}
                                        />
                                        {error && (
                                            <Text $error data-testid="landing-join-error">
                                                {error}
                                            </Text>
                                        )}
                                    </FormFields>
                                    <Row $justify="flex-start" $marginBottom={20}>
                                        <CheckboxWrapper>
                                            <input
                                                checked={joinForm.subscribeToNews}
                                                data-testid="landing-join-newsletter"
                                                disabled={loading}
                                                id="subscribe_to_news"
                                                name="subscribe_to_news"
                                                type="checkbox"
                                                onChange={e => {
                                                    const { checked } = e.currentTarget;
                                                    setJoinForm(prev => ({
                                                        ...prev,
                                                        subscribeToNews: checked,
                                                    }));
                                                }}
                                            />
                                            <span>Subscribe to Kromatic Newsletters</span>
                                        </CheckboxWrapper>
                                    </Row>
                                    <CenteredColumn>
                                        <MassiveButton
                                            data-testid="landing-join-submit"
                                            disabled={loading}
                                            type="submit"
                                        >
                                            Join now
                                        </MassiveButton>
                                    </CenteredColumn>
                                </>
                            )}
                        </Form>
                    </ModalContent>
                    <Backdrop
                        aria-label="Close auth modal"
                        data-testid="landing-auth-backdrop"
                        disabled={loading}
                        onClick={handleCloseModal}
                    />
                </ModalContainer>
            )}
        </>
    );
}
